import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin } from '../utils/api';
import { validateRequired } from '../utils/validation';
import { executeRecaptcha } from '../config/recaptcha';
import './AdminLoginPage.css';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const publicSiteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL || '').replace(/\/$/, '');
  const publicAsset = (path) => (publicSiteUrl ? `${publicSiteUrl}${path}` : path);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderFade, setPlaceholderFade] = useState(false);

  const placeholders = ['johndoe', 'johndoe@example.com'];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderFade(true);
      
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
        setPlaceholderFade(false);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, [placeholders.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!validateRequired(formData.username)) {
      newErrors.username = 'Používateľské meno alebo email je povinný';
    }
    
    if (!validateRequired(formData.password)) {
      newErrors.password = 'Heslo je povinné';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setGeneralError('');
      
      const recaptchaToken = await executeRecaptcha('login');
      
      if (!recaptchaToken) {
        setGeneralError('reCAPTCHA overenie zlyhalo. Skúste to znova.');
        setLoading(false);
        return;
      }
      
      const response = await adminLogin({
        username: formData.username,
        password: formData.password,
        recaptcha_token: recaptchaToken,
      });

      if (response.data.success) {
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      const errorMessage = error.response?.data?.message || 
        'Prihlásenie zlyhalo. Skontrolujte prihlasovacie údaje.';
      
      setGeneralError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img
              src={publicAsset('/assets/images/logos/Logo bez pozadia.webp')}
              alt="ML Servis"
              className="login-logo"
            />
            {/* <h1>Prihlásenie</h1> */}
            {/* <p className="text-muted">Admin panel - Rezervačný systém</p> */}
          </div>

          {generalError && (
            <div className="alert alert-error mb-3">
              {generalError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                Používateľské meno / Email
              </label>
              <div className="username-input-wrapper">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`form-input ${errors.username ? 'error' : ''} ${placeholderFade ? 'placeholder-fade' : ''}`}
                  placeholder={placeholders[placeholderIndex]}
                  autoComplete="username"
                />
              </div>
              {errors.username && (
                <span className="form-error">{errors.username}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Heslo
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              {errors.password && (
                <span className="form-error">{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-lg btn-block"
            >
              {loading ? 'Prihlasovanie...' : 'Prihlásiť sa'}
            </button>

            <div className="recaptcha-info">
              <svg className="recaptcha-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              <span className="recaptcha-text">
                Chránené službou reCAPTCHA
              </span>
            </div>
          </form>

          <div className="login-footer">
            <a href={publicSiteUrl || '/'} className="text-muted">
              ← Späť na hlavnú stránku
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
